<template>
  <div class="admin-users-view">
    <BreadcrumbBar 
      parentName="Admin"
      currentName="User Management"
      backRoute="/"
    />

    <div class="content">
      <!-- Toolbar -->
      <div class="content-toolbar">
        <div class="toolbar-left"></div>
        <div class="toolbar-right">
          <button class="btn-primary" @click="showInviteModal = true"><AppIcon name="plus" />Invite User</button>
        </div>
      </div>

      <!-- Users List -->
      <section class="section">
        <div v-if="loadingUsers" class="loading">Loading users...</div>
        <div v-else-if="users.length === 0" class="empty">No users found</div>
        <div v-else class="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Station Access</th>
                <th>Last Login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users" :key="user.id" :class="{ 'is-current': user.id === currentUserId }">
                <td>{{ user.name }}</td>
                <td>{{ user.email }}</td>
                <td>
                  <span :class="['badge', user.role]">{{ user.role }}</span>
                </td>
                <td>
                  <span v-if="user.role === 'admin'" class="text-muted">All stations</span>
                  <span v-else-if="user.stationAccess.length === 0" class="text-muted">None</span>
                  <span v-else>{{ user.stationAccess.length }} station(s)</span>
                </td>
                <td>
                  <span v-if="user.lastLoginAt">{{ formatDate(user.lastLoginAt) }}</span>
                  <span v-else class="text-muted">Never</span>
                </td>
                <td class="actions">
                  <button class="btn-icon" @click="editUser(user)" title="Edit"><AppIcon name="edit" /></button>
                  <button 
                    v-if="user.id !== currentUserId" 
                    class="btn-icon danger" 
                    @click="confirmDeleteUser(user)" 
                    title="Delete"
                  ><AppIcon name="trash" /></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Pending Invites -->
      <section class="section">
        <h2>Pending Invites</h2>
        <div v-if="loadingInvites" class="loading">Loading invites...</div>
        <div v-else-if="invites.length === 0" class="empty">No pending invites</div>
        <div v-else class="table-container">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Expires</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="invite in invites" :key="invite.id">
                <td>{{ invite.email }}</td>
                <td>
                  <span :class="['badge', invite.role]">{{ invite.role }}</span>
                </td>
                <td>{{ formatDate(invite.expiresAt) }}</td>
                <td class="actions">
                  <button class="btn-icon" @click="copyInviteLink(invite)" title="Copy invite link"><AppIcon name="copy" /></button>
                  <button class="btn-icon danger" @click="deleteInvite(invite)" title="Cancel"><AppIcon name="trash" /></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <!-- Invite Modal -->
    <div v-if="showInviteModal" class="modal-overlay">
      <div class="modal">
        <h2>Invite User</h2>
        <form @submit.prevent="sendInvite">
          <div class="form-group">
            <label>Email *</label>
            <input v-model="inviteForm.email" type="email" placeholder="user@example.com" required>
          </div>
          <div class="form-group">
            <label>Role</label>
            <select v-model="inviteForm.role">
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div v-if="inviteForm.role === 'member'" class="form-group">
            <label>Station Access</label>
            <div class="checkbox-list">
              <label class="checkbox-item checkbox-select-all">
                <input type="checkbox"
                  :checked="inviteForm.stationAccess.length === stations.length && stations.length > 0"
                  :indeterminate.prop="inviteForm.stationAccess.length > 0 && inviteForm.stationAccess.length < stations.length"
                  @change="inviteForm.stationAccess = $event.target.checked ? stations.map(s => s.id) : []"
                > <strong>Select All</strong>
              </label>
              <label v-for="station in stations" :key="station.id" class="checkbox-item">
                <input type="checkbox" :value="station.id" v-model="inviteForm.stationAccess"> {{ station.name }}
              </label>
            </div>
          </div>
          
          <div v-if="inviteError" class="error-message">{{ inviteError }}</div>
          
          <div v-if="inviteUrl" class="invite-url-box">
            <label>Invite URL (copy and share):</label>
            <div class="url-row">
              <input type="text" :value="inviteUrl" readonly>
              <button type="button" @click="copyInviteUrl"><AppIcon name="copy" /></button>
            </div>
          </div>
          
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeInviteModal">Close</button>
            <button v-if="!inviteUrl" type="submit" class="btn-primary" :disabled="inviting">
              {{ inviting ? 'Sending...' : 'Send Invite' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal">
        <h2>Edit User</h2>
        <form @submit.prevent="updateUser">
          <div class="form-group">
            <label>Name</label>
            <input v-model="editForm.name" type="text" required>
          </div>
          <div class="form-group">
            <label>Email</label>
            <input :value="editForm.email" type="email" disabled>
          </div>
          <div class="form-group">
            <label>Role</label>
            <select v-model="editForm.role" :disabled="editForm.id === currentUserId">
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div v-if="editForm.role === 'member'" class="form-group">
            <label>Station Access</label>
            <div class="checkbox-list">
              <label class="checkbox-item checkbox-select-all">
                <input type="checkbox"
                  :checked="editForm.stationAccess.length === stations.length && stations.length > 0"
                  :indeterminate.prop="editForm.stationAccess.length > 0 && editForm.stationAccess.length < stations.length"
                  @change="editForm.stationAccess = $event.target.checked ? stations.map(s => s.id) : []"
                > <strong>Select All</strong>
              </label>
              <label v-for="station in stations" :key="station.id" class="checkbox-item">
                <input type="checkbox" :value="station.id" v-model="editForm.stationAccess"> {{ station.name }}
              </label>
            </div>
          </div>
          
          <div v-if="editError" class="error-message">{{ editError }}</div>
          
          <div class="modal-actions">
            <button type="button" class="btn-secondary" @click="closeEditModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="updating">
              {{ updating ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    <!-- Delete User Confirmation Modal -->
    <ConfirmModal
      :visible="showDeleteUserModal"
      title="Delete User"
      :message="userToDelete ? `Are you sure you want to delete ${userToDelete.name}? This action cannot be undone.` : ''"
      type="danger"
      confirmText="Delete"
      :loading="deletingUser"
      loadingText="Deleting..."
      @confirm="doDeleteUser"
      @cancel="showDeleteUserModal = false; userToDelete = null"
    />
    
    <!-- Cancel Invite Confirmation Modal -->
    <ConfirmModal
      :visible="showDeleteInviteModal"
      title="Cancel Invite"
      :message="inviteToDelete ? `Cancel the invite for ${inviteToDelete.email}?` : ''"
      type="warning"
      confirmText="Cancel Invite"
      :loading="deletingInvite"
      loadingText="Canceling..."
      @confirm="doDeleteInvite"
      @cancel="showDeleteInviteModal = false; inviteToDelete = null"
    />
    
    <!-- Error Modal -->
    <ConfirmModal
      :visible="showErrorModal"
      title="Error"
      :message="errorMessage"
      type="danger"
      confirmText="OK"
      :showCancel="false"
      @confirm="showErrorModal = false"
    />
    
    <!-- Success Modal -->
    <ConfirmModal
      :visible="showSuccessModal"
      title="Success"
      :message="successMessage"
      type="default"
      confirmText="OK"
      :showCancel="false"
      @confirm="showSuccessModal = false"
    />
  </div>
</template>

<script>
import { adminApi, fetchStations } from '../services/api'
import ConfirmModal from '../components/ConfirmModal.vue'
import BreadcrumbBar from '../components/BreadcrumbBar.vue'

export default {
  name: 'AdminUsersView',
  components: { ConfirmModal, BreadcrumbBar },
  data() {
    return {
      users: [],
      invites: [],
      stations: [],
      loadingUsers: true,
      loadingInvites: true,
      
      showInviteModal: false,
      inviteForm: { email: '', role: 'member', stationAccess: [] },
      inviteUrl: null,
      inviteError: null,
      inviting: false,
      
      showEditModal: false,
      editForm: { id: '', name: '', email: '', role: 'member', stationAccess: [] },
      editError: null,
      updating: false,
      
      // Delete/Cancel confirmation modals
      showDeleteUserModal: false,
      userToDelete: null,
      deletingUser: false,
      showDeleteInviteModal: false,
      inviteToDelete: null,
      deletingInvite: false,
      // Error modal
      showErrorModal: false,
      errorMessage: '',
      // Success modal (for copy feedback)
      showSuccessModal: false,
      successMessage: ''
    }
  },
  computed: {
    currentUserId() {
      return this.$store.getters['auth/user']?.id
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      await Promise.all([
        this.loadUsers(),
        this.loadInvites(),
        this.loadStations()
      ])
    },
    
    async loadUsers() {
      this.loadingUsers = true
      try {
        const data = await adminApi.getUsers()
        this.users = data.users
      } catch (err) {
        console.error('Failed to load users:', err)
      } finally {
        this.loadingUsers = false
      }
    },
    
    async loadInvites() {
      this.loadingInvites = true
      try {
        const data = await adminApi.getInvites()
        this.invites = data.invites
      } catch (err) {
        console.error('Failed to load invites:', err)
      } finally {
        this.loadingInvites = false
      }
    },
    
    async loadStations() {
      try {
        const data = await fetchStations()
        this.stations = data.stations
      } catch (err) {
        console.error('Failed to load stations:', err)
      }
    },
    
    async sendInvite() {
      this.inviteError = null
      this.inviting = true
      
      try {
        const data = await adminApi.createInvite({
          email: this.inviteForm.email,
          role: this.inviteForm.role,
          stationAccess: this.inviteForm.role === 'member' ? this.inviteForm.stationAccess : []
        })
        this.inviteUrl = data.inviteUrl
        await this.loadInvites()
      } catch (err) {
        this.inviteError = err.message
      } finally {
        this.inviting = false
      }
    },
    
    closeInviteModal() {
      this.showInviteModal = false
      this.inviteForm = { email: '', role: 'member', stationAccess: [] }
      this.inviteUrl = null
      this.inviteError = null
    },
    
    copyInviteUrl() {
      navigator.clipboard.writeText(this.inviteUrl)
    },
    
    editUser(user) {
      this.editForm = {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        stationAccess: [...(user.stationAccess || [])]
      }
      this.editError = null
      this.showEditModal = true
    },
    
    closeEditModal() {
      this.showEditModal = false
      this.editForm = { id: '', name: '', email: '', role: 'member', stationAccess: [] }
      this.editError = null
    },
    
    async updateUser() {
      this.editError = null
      this.updating = true
      
      try {
        await adminApi.updateUser(this.editForm.id, {
          name: this.editForm.name,
          role: this.editForm.role,
          stationAccess: this.editForm.role === 'member' ? this.editForm.stationAccess : []
        })
        await this.loadUsers()
        this.closeEditModal()
      } catch (err) {
        this.editError = err.message
      } finally {
        this.updating = false
      }
    },
    
    confirmDeleteUser(user) {
      this.userToDelete = user
      this.showDeleteUserModal = true
    },
    
    async doDeleteUser() {
      if (!this.userToDelete) return
      this.deletingUser = true
      
      try {
        await adminApi.deleteUser(this.userToDelete.id)
        this.showDeleteUserModal = false
        this.userToDelete = null
        await this.loadUsers()
      } catch (err) {
        this.showDeleteUserModal = false
        this.errorMessage = 'Failed to delete user: ' + err.message
        this.showErrorModal = true
      } finally {
        this.deletingUser = false
      }
    },
    
    deleteInvite(invite) {
      this.inviteToDelete = invite
      this.showDeleteInviteModal = true
    },
    
    copyInviteLink(invite) {
      const inviteUrl = `${window.location.origin}/invite/${invite.token}`
      navigator.clipboard.writeText(inviteUrl).then(() => {
        this.successMessage = 'Invite link copied to clipboard!'
        this.showSuccessModal = true
      }).catch(err => {
        console.error('Failed to copy:', err)
        this.errorMessage = 'Failed to copy link to clipboard'
        this.showErrorModal = true
      })
    },
    
    async doDeleteInvite() {
      if (!this.inviteToDelete) return
      this.deletingInvite = true
      
      try {
        await adminApi.deleteInvite(this.inviteToDelete.id)
        this.showDeleteInviteModal = false
        this.inviteToDelete = null
        await this.loadInvites()
      } catch (err) {
        this.showDeleteInviteModal = false
        this.errorMessage = 'Failed to cancel invite: ' + err.message
        this.showErrorModal = true
      } finally {
        this.deletingInvite = false
      }
    },
    
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.admin-users-view {
  min-height: calc(100vh - 74px);
  background: var(--gray-50);
}

.content {
  padding: 32px;
  max-width: 1200px;
  margin: 0 auto;
}

.content-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section {
  background: var(--white);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
}

.section h2 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.loading, .empty {
  color: var(--gray-500);
  font-size: 14px;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid var(--gray-100);
  font-size: 14px;
}

th {
  font-weight: 500;
  color: var(--gray-500);
  font-size: 12px;
  text-transform: uppercase;
}

tr.is-current {
  background: var(--gray-50);
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge.admin {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge.member {
  background: var(--gray-100);
  color: var(--gray-600);
}

.text-muted {
  color: var(--gray-400);
}

.actions {
  display: flex;
  gap: 8px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  background: var(--white);
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon:hover {
  background: var(--gray-50);
}

.btn-icon.danger:hover {
  background: #fef2f2;
  border-color: #fecaca;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--white);
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
}

.form-group input:not([type="checkbox"]),
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  font-size: 14px;
}

.form-group input:disabled {
  background: var(--gray-50);
  color: var(--gray-500);
}

.checkbox-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  padding: 12px;
}

.checkbox-select-all {
  border-bottom: 1px solid var(--gray-200);
  margin-bottom: 6px;
  padding-bottom: 8px !important;
}

.checkbox-item {
  display: flex !important;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 400 !important;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 0 !important;
}

.checkbox-item input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

.error-message {
  background: #fef2f2;
  color: #dc2626;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 16px;
}

.invite-url-box {
  margin-bottom: 16px;
}

.invite-url-box label {
  display: block;
  font-size: 12px;
  color: var(--gray-500);
  margin-bottom: 6px;
}

.url-row {
  display: flex;
  gap: 8px;
}

.url-row input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  font-size: 12px;
  background: var(--gray-50);
}

.url-row button {
  padding: 8px 12px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  cursor: pointer;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn-primary {
  padding: 10px 20px;
  background: var(--accent);
  color: var(--white);
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 10px 20px;
  background: var(--white);
  color: var(--gray-700);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn-secondary:hover {
  background: var(--gray-50);
}
</style>
